import {computed, defineComponent, type PropType, watch} from 'vue';
import {useFormatting} from "@/composables/currency-format.ts";
import type {BasketItem} from "@/app/stores/session-store.ts";

export default defineComponent({
    name: 'AegServiceBasket',
    props: {
        paymentType: {
            type: String,
            required: true
        },
        basketItems: {
            type: Object as PropType<BasketItem[]>,
            required: true
        }
    },
    emits: ['on-remove', 'on-vat-total-negative', 'on-total-amount-excl-vat-negative'],
    setup(props, {emit}) {
        const {formatCurrency, roundToCurrency} = useFormatting();

        const totalAmountInclVat = computed(() => {
            // Calculate the total amount without rounding
            const total = props.basketItems.reduce((acc, item) => {
                const amount = item.amount ?? 1;
                const priceInclVat = item.priceExclVat * (1 + item.vatPercentage / 100);
                return acc + priceInclVat * amount;
            }, 0);

            // Apply final rounding to the total amount
            return roundToCurrency(total);
        });

        const totalAmountExclVat = computed(() => {
            return props.basketItems.reduce((total, item) => {
                const amount = item.amount ?? 1;
                return total + item.priceExclVat * amount;
            }, 0);
        });

        const basketTitleText = computed((): string => {
            if (props.paymentType === 'invoice') {
                return 'Factuurdata';
            }

            if (props.paymentType === 'purchase') {
                return 'Inkoopfactuurdata';
            }

            return 'Winkelmandje voor betaalsessie';
        });

        const vatTotals = computed(() => {
            const totals = new Map<number, number>();
            props.basketItems.forEach(item => {
                const amount = item.amount ?? 1;
                const vatAmount = item.priceExclVat * (item.vatPercentage / 100) * amount;
                const currentTotal = totals.get(item.vatPercentage) ?? 0;
                totals.set(item.vatPercentage, currentTotal + vatAmount);
            });
            return Array.from(totals.entries()).map(([vatPercentage, vatAmount]) => ({
                vatPercentage,
                vatAmount: roundToCurrency(vatAmount)
            }));
        });

        const totalVatAmount = computed(() => {
            return props.basketItems.reduce((total, item) => {
                const amount = item.amount ?? 1;
                return total + item.priceExclVat * (item.vatPercentage / 100) * amount;
            }, 0);
        });

        watch(totalVatAmount, (total) => {
            emit('on-vat-total-negative', total < 0);
        }, {immediate: true});

        watch(totalAmountExclVat, (total) => {
            emit('on-total-amount-excl-vat-negative', total < 0);
        }, {immediate: true});

        const removeItemFromBasket = (item: BasketItem) => {
            emit('on-remove', item);
        }

        return {
            basketTitleText,
            formatCurrency,
            removeItemFromBasket,
            totalAmountInclVat,
            vatTotals
        }
    }
})
